import { useState, useRef } from 'react';
import { Plus, Upload, X } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { getHubColor } from '../../../hooks/useSidebarData';
import { BACKEND_URL } from '@/config/api';

export const CreateHubModal = ({ 
  isOpen, 
  onClose, 
  token, 
  hubCount,
  workspaceId,
  onHubCreated 
}) => {
  const [hubName, setHubName] = useState('');
  const [description, setDescription] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Only JPG, PNG, and WebP images are allowed');
      return;
    }

    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogo(reader.result);
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    if (!hubName.trim()) return;
    
    setIsLoading(true);
    try {
      const payload = { 
        name: hubName,
        description: description || undefined,
        color: getHubColor(hubCount || 0)
      };
      
      // Add workspace_id if provided
      if (workspaceId) {
        payload.workspace_id = workspaceId;
      }
      
      // Add logo if uploaded
      if (logo) {
        payload.logo = logo;
      }

      const response = await fetch(`${BACKEND_URL}/api/hubs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const newHub = await response.json();
        onHubCreated?.(newHub);
        handleClose();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create hub');
      }
    } catch (error) {
      console.error('Error creating hub:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setHubName('');
    setDescription('');
    setLogo(null);
    setLogoPreview(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Create a new hub</DialogTitle>
          <DialogDescription className="text-white/60">
            Hubs are project containers for organizing your team's boards, channels, groups, and canvas.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Hub Logo Upload */}
          <div className="space-y-2">
            <Label className="text-white/80">Hub logo (optional)</Label>
            <div className="flex items-center gap-4">
              <div className="relative">
                {logoPreview ? (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Hub logo preview" 
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    <button
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-xl font-bold border-2 border-dashed border-white/30 hover:border-white/50 cursor-pointer transition-colors"
                    style={{ backgroundColor: hubName ? getHubColor(hubCount || 0) : 'transparent' }}
                  >
                    {hubName ? hubName.substring(0, 2).toUpperCase() : (
                      <Upload size={20} className="text-white/40" />
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoChange}
                  className="hidden"
                  data-testid="hub-logo-input"
                />
              </div>
              <div className="text-xs text-white/40">
                <p>Square image recommended.</p>
                <p>Max 2MB, JPG, PNG, or WebP</p>
                {!logoPreview && (
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="text-blue-400 hover:text-blue-300 mt-1"
                  >
                    Click to upload
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Hub Name */}
          <div className="space-y-2">
            <Label htmlFor="hub-name" className="text-white/80">Hub name *</Label>
            <Input
              id="hub-name"
              value={hubName}
              onChange={(e) => setHubName(e.target.value)}
              placeholder="e.g. Acme Marketing"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
              data-testid="hub-name-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="hub-description" className="text-white/80">Description (optional)</Label>
            <Input
              id="hub-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this hub about?"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!hubName.trim() || isLoading}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="create-hub-submit"
          >
            {isLoading ? 'Creating...' : 'Create hub'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
